/**
 * Role Guard Component
 * Protects routes and UI elements based on user roles
 */

import { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { Box, Alert, AlertIcon, AlertTitle, AlertDescription } from '@chakra-ui/react';
import { useAuth } from '../../contexts/AuthContext';
import { UserRole } from '../../types/permissions';

interface RoleGuardProps {
  children: ReactNode;
  allowedRoles: UserRole[];
  redirectTo?: string;
  fallback?: ReactNode;
  showError?: boolean;
}

/**
 * RoleGuard - Protects content based on user role
 * 
 * @param children - Content to protect
 * @param allowedRoles - Array of roles that can access the content
 * @param redirectTo - Path to redirect if access denied (default: '/admin')
 * @param fallback - Custom component to show instead of redirect
 * @param showError - Show error message instead of redirect (default: false)
 * 
 * @example
 * // Protect a route for admins only
 * <RoleGuard allowedRoles={['admin', 'superadmin']}>
 *   <AdminPanel />
 * </RoleGuard>
 * 
 * @example
 * // Show custom fallback
 * <RoleGuard 
 *   allowedRoles={['superadmin']} 
 *   fallback={<AccessDenied />}
 * >
 *   <DangerZone />
 * </RoleGuard>
 */
export function RoleGuard({
  children,
  allowedRoles,
  redirectTo = '/admin',
  fallback,
  showError = false,
}: RoleGuardProps) {
  const { userProfile, loading } = useAuth();

  // Show loading state
  if (loading) {
    return (
      <Box p={4} textAlign="center">
        Loading...
      </Box>
    );
  }

  // No user - redirect to login
  if (!userProfile) {
    return <Navigate to="/signin" replace />;
  }

  // Check if user's role is allowed
  const hasAccess = allowedRoles.includes(userProfile.role);

  if (!hasAccess) {
    // Show custom fallback if provided
    if (fallback) {
      return <>{fallback}</>;
    }

    // Show error message
    if (showError) {
      return (
        <Box p={8} maxW="600px" mx="auto">
          <Alert
            status="error"
            variant="subtle"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            textAlign="center"
            height="200px"
            borderRadius="md"
          >
            <AlertIcon boxSize="40px" mr={0} />
            <AlertTitle mt={4} mb={1} fontSize="lg">
              Access Denied
            </AlertTitle>
            <AlertDescription maxWidth="sm">
              You don't have permission to access this content. Your role is{' '}
              <strong>{userProfile.role}</strong>. Required roles:{' '}
              {allowedRoles.join(', ')}.
            </AlertDescription>
          </Alert>
        </Box>
      );
    }

    // Redirect
    return <Navigate to={redirectTo} replace />;
  }

  // User has access
  return <>{children}</>;
}

interface RoleGuardInlineProps {
  children: ReactNode;
  allowedRoles: UserRole[];
  fallback?: ReactNode;
}

/**
 * RoleGuardInline - Conditionally render content based on role (no redirect)
 * Useful for hiding/showing UI elements without navigation
 * 
 * @example
 * <RoleGuardInline allowedRoles={['admin', 'superadmin']}>
 *   <DeleteButton />
 * </RoleGuardInline>
 */
export function RoleGuardInline({
  children,
  allowedRoles,
  fallback = null,
}: RoleGuardInlineProps) {
  const { userProfile, loading } = useAuth();

  if (loading || !userProfile) {
    return <>{fallback}</>;
  }

  const hasAccess = allowedRoles.includes(userProfile.role);

  return <>{hasAccess ? children : fallback}</>;
}
